import type {SessionState} from '@github-ui/agent-sessions/types/session'
import {generateTaskPathForRepo} from '@github-ui/agent-sessions/utils/generate-task-path'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {testIdProps} from '@github-ui/test-id-props'
import {CopilotIcon} from '@primer/octicons-react'
import {Token} from '@primer/react'
import {clsx} from 'clsx'
import {memo, useMemo} from 'react'

import type {AgentInfo, AgentTask} from '../../api/memex-items/contracts'
import {isCopilot} from '../../helpers/assignee-helper'
import {AgentTaskPillStrings} from '../../strings'
import {tokenIconSize} from '../common/avatars-token'
import {isProgressTask, shouldShowAgentTaskPill} from '../table/agent-task-utils'
import type {AgentTaskPopoverTaskItem} from './agent-task-popover'
import {AgentTaskPopover} from './agent-task-popover'

const MAX_POPOVER_TASKS = 5

/**
 * Given an array of agent tasks, returns the latest visible task per unique agent.
 * Tasks are grouped by agent login. For each group, only the most recently created
 * task that passes the `shouldShowAgentTaskPill` check is returned.
 */
export function getLatestAgentTaskPerAgent(tasks: Array<AgentTask>): Array<AgentTask> {
  const latestPerAgent = new Map<string | undefined, AgentTask>()

  for (const task of tasks) {
    if (!shouldShowAgentTaskPill(task.state)) continue

    const key = task.agent?.login
    const existing = latestPerAgent.get(key)
    if (!existing || new Date(task.createdAt ?? 0) > new Date(existing.createdAt ?? 0)) {
      latestPerAgent.set(key, task)
    }
  }

  return [...latestPerAgent.values()]
}

/**
 * Returns the list of agent task pills to display for a memex item.
 * Groups by agent and returns the latest task per agent.
 * Returns an empty array when the feature flag is disabled or there are no tasks.
 */
export function useAgentTaskPillsToShow(item: {agentTasks?: Array<AgentTask>}): Array<AgentTask> {
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
    TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
    https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const featureEnabled = useFeatureFlag('projects_copilot_tasks_status')

  return useMemo(() => {
    if (!featureEnabled || !item.agentTasks || item.agentTasks.length === 0) return []

    return getLatestAgentTaskPerAgent(item.agentTasks)
  }, [featureEnabled, item.agentTasks])
}

/**
 * Builds the filtered task list for the popover from a set of tasks belonging
 * to a single agent. Prioritizes in-progress tasks, limited to MAX_POPOVER_TASKS.
 */
export function buildPopoverTaskList(tasks: Array<AgentTask>): Array<AgentTaskPopoverTaskItem> {
  const visible = tasks.filter(t => shouldShowAgentTaskPill(t.state))

  const inProgress = visible.filter(t => isProgressTask(t))
  const rest = visible.filter(t => !isProgressTask(t))
  const capped = [...inProgress, ...rest].slice(0, MAX_POPOVER_TASKS)

  return capped.map(task => {
    const href = task.id
      ? generateTaskPathForRepo({taskId: task.id, repositoryNwo: task.repoNwo}) || undefined
      : undefined
    return {
      id: task.id,
      state: task.state,
      title: task.title,
      nwo: task.repoNwo,
      createdAt: task.createdAt,
      lastUpdatedAt: task.lastUpdatedAt,
      href,
    }
  })
}

interface AgentTaskPillProps {
  state: SessionState
  agent?: AgentInfo
  className?: string
  size?: 'small' | 'medium' | 'large' | 'xlarge'
  sessionUrl?: string
  agentTasks?: Array<AgentTask>
}

function getDisplayLabel(state: SessionState): string {
  switch (state) {
    case 'in_progress':
    case 'queued':
      return AgentTaskPillStrings.inProgress
    case 'completed':
      return AgentTaskPillStrings.complete
    case 'failed':
    case 'timed_out':
      return AgentTaskPillStrings.failed
    case 'cancelled':
      return AgentTaskPillStrings.cancelled
    case 'waiting_for_user':
      return AgentTaskPillStrings.waiting
    default:
      return AgentTaskPillStrings.inProgress
  }
}

function AgentIcon({agent, size}: {agent?: AgentInfo; size?: number}) {
  const iconSize = size ?? 16

  if (agent && !isCopilot(agent.login) && agent.avatarUrl) {
    return <GitHubAvatar src={agent.avatarUrl} alt={agent.displayName || agent.login} size={iconSize} />
  }

  return <CopilotIcon size={iconSize} />
}

export const AgentTaskPill = memo(function AgentTaskPill({
  state,
  agent,
  className,
  size,
  sessionUrl,
  agentTasks,
}: AgentTaskPillProps) {
  const label = getDisplayLabel(state)
  const iconSize = tokenIconSize(size)

  const popoverTasks = useMemo(() => {
    if (!agentTasks || agentTasks.length === 0) return []
    return buildPopoverTaskList(agentTasks)
  }, [agentTasks])

  const isInteractive = Boolean(sessionUrl || popoverTasks.length > 0)
  const pillClassNames = clsx('color-fg-default', isInteractive && 'cursor-pointer', className)

  const pill = sessionUrl ? (
    <Token
      as="a"
      href={sessionUrl}
      target="_blank"
      rel="noreferrer"
      leadingVisual={() => <AgentIcon agent={agent} size={iconSize} />}
      text={label}
      size={size}
      className={pillClassNames}
      {...testIdProps('agent-task-pill')}
    />
  ) : (
    <Token
      as="span"
      leadingVisual={() => <AgentIcon agent={agent} size={iconSize} />}
      text={label}
      size={size}
      className={pillClassNames}
      {...testIdProps('agent-task-pill')}
    />
  )

  return <AgentTaskPopover tasks={popoverTasks}>{pill}</AgentTaskPopover>
})
